// src/App.js
import React, { useState, useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { loadHHPData } from './data/hhpData';
import { loadGrapeIndex } from './utils/grapeIndex';
import SearchTool from './components/SearchTool';
import AdminPanel from './components/AdminPanel';
import LoginModal from './components/LoginModal';
import './App.css';

function DisclaimerModal({ onClose }) {
  return (
    <div className="modal-overlay disclaimer-overlay">
      <div className="modal disclaimer-modal">
        <div className="modal-header">
          <h2>Important Notice</h2>
        </div>
        <div className="modal-body">
          <div className="disclaimer-modal-icon">⚠</div>
          <p className="disclaimer-modal-text">
            This tool is provided as a courtesy of Sustainable WA and <strong>DOES NOT WARRANTY</strong> the
            search results. Always consult the label prior to use for both legality in your state, crop,
            and intended use as well as compliance with Sustainable WA standards.
            Compliant application is the responsibility of the grower.
          </p>
          <button className="disclaimer-modal-btn" onClick={onClose}>
            I Understand — Continue to Tool
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { user, isAdmin, loading, login, logout } = useAuth();
  const [showLogin, setShowLogin]   = useState(false);
  const [showAdmin, setShowAdmin]   = useState(false);
  const [hhpReady, setHhpReady]     = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  useEffect(() => {
    Promise.all([loadHHPData(), loadGrapeIndex()]).then(() => setHhpReady(true));
  }, []);

  if (loading || !hhpReady) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
        {!loading && <p className="loading-label">Loading HHP database...</p>}
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header */}
      <header className="site-header">
        <div className="header-inner">
          <div className="header-logo">
            <img
              src="https://i0.wp.com/sustainablewa.com/wp-content/uploads/2023/02/SW_Umbrella_Green_White.png?fit=2090%2C560&ssl=1"
              alt="Sustainable WA"
              className="logo-img"
            />
          </div>
          <nav className="header-nav">
            <a href="https://sustainablewa.com/the-standard/" target="_blank" rel="noreferrer">The Standard</a>
            <a href="https://sustainablewa.com/about-us/" target="_blank" rel="noreferrer">About</a>
            {isAdmin ? (
              <>
                <button className="nav-btn admin-btn" onClick={() => setShowAdmin(true)}>Admin Panel</button>
                <button className="nav-btn logout-btn" onClick={logout}>Sign Out</button>
              </>
            ) : (
              <button className="nav-btn login-btn" onClick={() => setShowLogin(true)}>Admin Login</button>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="hero-texture" />
        <div className="hero-content">
          <div className="hero-eyebrow">Sustainable WA</div>
          <h1 className="hero-title">Material Safety<br />Lookup Tool</h1>
          <p className="hero-subtitle">
            Check whether a pesticide product or active ingredient appears on the
            Highly Hazardous Pesticides (HHP) Watch List or Prohibited Materials list —
            and verify its registration status in Washington and Oregon.
          </p>
          <p className="hero-subtitle hero-subtitle-note">
            Expired labels will show as not labeled for a state. If a product has been
            registered with the state for legal use that will appear as well. In this
            case the same EPA Registration # may be used.
          </p>
          <div className="hero-key">
            <div className="key-section">
              <div className="key-label">HHP STATUS</div>
              <div className="key-items">
                <span className="badge badge-prohibited key-item" title="This material is on the international prohibited list and must not be used.">⛔ Prohibited Material</span>
                <span className="badge badge-watch key-item" title="This material appears on the HHP Watch List due to acute toxicity, long-term health, or environmental risk concerns.">⚠ HHP Watch List</span>
                <span className="badge badge-clear key-item" title="This material does not appear on the HHP Prohibited or Watch List.">✓ Not on HHP List</span>
              </div>
            </div>
            <div className="key-section">
              <div className="key-label">STATE REGISTRATION</div>
              <div className="key-items">
                <span className="key-chip chip-legal-eg key-item" title="This product is currently registered for legal use in this state.">WA ✓ / OR ✓ — Legal for use in state</span>
                <span className="key-chip chip-illegal-eg key-item" title="This product does not appear as registered in this state in the PICOL database and is not legal for use.">WA ✗ / OR ✗ — Not registered in state</span>
              </div>
            </div>
            <div className="key-section">
              <div className="key-label">GRAPE LABELING</div>
              <div className="key-items">
                <span className="key-chip grape-yes key-item" title="This product is labeled for use on grapes in this state according to PICOL.">🍇 WA ✓ / OR ✓ — Labeled for use on grapes</span>
                <span className="key-chip grape-no key-item" title="This product does NOT appear as labeled for use on grapes in this state. Do not use on grapes.">🍇 WA ✗ / OR ✗ — NOT labeled for grapes</span>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* Main search tool */}
      <main className="main-content">
        <SearchTool />
      </main>

      {/* Disclaimer */}
      <section className="disclaimer-section">
        <div className="disclaimer-inner">
          <h3>About This Tool</h3>
          <p>
            This tool cross-references product data from the{' '}
            <a href="https://picol.cahnrs.wsu.edu" target="_blank" rel="noreferrer">
              WSU PICOL database
            </a>{' '}
            with the WA Winegrowers Highly Hazardous Pesticides list. A product not found in
            PICOL for Washington State is not labeled for use in WA, regardless of its HHP
            classification. Always consult the current product label and your local extension
            office before applying any pesticide. This tool is provided for informational
            purposes only and does not constitute legal or regulatory advice.
          </p>
          <p className="disclaimer-update">
            HHP List version: <strong>SustainableWA v2.19 / PAN HHP December 2024</strong> · PICOL data: live via WSU API
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-inner">
          <img
            src="https://i0.wp.com/sustainablewa.com/wp-content/uploads/2023/02/SW_Umbrella_Green_White.png?fit=2090%2C560&ssl=1"
            alt="Sustainable WA"
            className="footer-logo"
          />
          <p className="footer-tagline">
            A tool by Sustainable WA — Washington State's sustainability program for vineyards.
          </p>
          <p className="footer-partners">
            A partnership between The Washington Winegrowers Association, The Washington Wine Commission,
            The Washington Wine Industry Foundation, and The Washington Wine Institute.
          </p>
        </div>
      </footer>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <DisclaimerModal onClose={() => setShowDisclaimer(false)} />
      )}

      {/* Modals */}
      {showLogin && (
        <LoginModal
          onLogin={login}
          onClose={() => setShowLogin(false)}
        />
      )}
      {showAdmin && isAdmin && (
        <AdminPanel onClose={() => setShowAdmin(false)} />
      )}
    </div>
  );
}
